import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, FileSignature } from "lucide-react";

const fmt = (d) => { try { return new Date(d).toLocaleString(); } catch { return d; } };

export function Agreements() {
    const [data, setData] = useState(null);
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = () => api.get("/admin/agreements").then((r) => setData(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);
    if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>;

    const startNew = (a) => setForm({ slug: a.slug, title: a.title, version: bump(a.version), body: a.body });
    const publish = async () => {
        setBusy(true);
        try { await api.post("/admin/agreements", form); toast.success(`Published v${form.version}`); setForm(null); await load(); }
        catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };

    return (
        <div className="space-y-8" data-testid="admin-agreements">
            <section>
                <h3 className="mb-3 font-heading text-lg font-bold text-navy">Agreement versions</h3>
                <div className="grid gap-4 md:grid-cols-2">
                    {data.agreements.map((a) => (
                        <div key={a.id} className={`glass-card rounded-2xl p-5 ${a.active ? "ring-1 ring-teal/40" : "opacity-70"}`} data-testid={`agreement-${a.slug}-${a.version}`}>
                            <div className="flex items-center justify-between"><div className="font-heading font-bold text-navy">{a.title}</div><Badge className={a.active ? "bg-teal/15 text-teal hover:bg-teal/15" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}>{a.active ? `v${a.version} · current` : `v${a.version}`}</Badge></div>
                            <div className="mt-1 text-xs text-slate-500">{a.slug} · published {fmt(a.published_at)} by {a.published_by}</div>
                            <p className="mt-3 line-clamp-3 text-sm text-slate-500">{a.body}</p>
                            {a.active && <Button size="sm" onClick={() => startNew(a)} data-testid={`agreement-new-version-${a.slug}`} className="mt-4 bg-navy text-white hover:bg-ocean"><FileSignature className="mr-2 h-4 w-4" /> Draft new version</Button>}
                        </div>
                    ))}
                </div>
            </section>

            {form && (
                <section className="glass-card rounded-2xl p-6" data-testid="agreement-editor">
                    <h3 className="font-heading text-lg font-bold text-navy">Publish new version</h3>
                    <p className="mt-1 text-sm text-slate-500">Publishing makes this the current version; clients who signed an older version will be asked to sign again before their next program consultation.</p>
                    <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_140px]">
                        <div><Label className="text-slate-700">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="agreement-title-input" className="mt-1 border-slate-200 bg-white text-navy" /></div>
                        <div><Label className="text-slate-700">Version</Label><Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} data-testid="agreement-version-input" className="mt-1 border-slate-200 bg-white text-navy" /></div>
                    </div>
                    <Label className="mt-4 block text-slate-700">Agreement text</Label>
                    <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={14} data-testid="agreement-body-input" className="mt-1 border-slate-200 bg-white text-sm text-slate-700" />
                    <div className="mt-4 flex gap-2">
                        <Button onClick={publish} disabled={busy} data-testid="agreement-publish-btn" className="bg-teal font-semibold text-white hover:bg-teal/90">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish version"}</Button>
                        <Button variant="outline" onClick={() => setForm(null)} className="border-slate-300 text-slate-700 hover:bg-slate-100">Cancel</Button>
                    </div>
                </section>
            )}

            <section>
                <h3 className="mb-3 font-heading text-lg font-bold text-navy">Signatures <span className="text-sm font-normal text-slate-500">({data.signatures.length})</span></h3>
                <div className="glass-card overflow-hidden rounded-2xl" data-testid="agreement-signatures">
                    <table className="w-full text-left text-sm">
                        <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500"><tr>{["Signed as", "Account", "Agreement", "Version", "When", "IP"].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
                        <tbody className="divide-y divide-slate-100">
                            {data.signatures.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No signatures yet.</td></tr>}
                            {data.signatures.map((s) => (
                                <tr key={s.id} className="text-slate-700"><td className="px-4 py-3 font-semibold text-navy">{s.full_name}</td><td className="px-4 py-3">{s.email}</td><td className="px-4 py-3">{s.slug}</td><td className="px-4 py-3">v{s.version}</td><td className="px-4 py-3 text-slate-500">{fmt(s.signed_at)}</td><td className="px-4 py-3 font-mono text-xs text-slate-500">{s.ip}</td></tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}

function bump(v) { const p = String(v).split("."); p[p.length - 1] = String((parseInt(p[p.length - 1], 10) || 0) + 1); return p.join("."); }
